import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    projectId: Number,
    msgProgressGenerating: { type: String, default: "프로필 이미지 생성 중..." },
    msgProgressComplete: { type: String, default: "프로필 이미지 생성 완료" },
    msgPersonaSaveSummary: { type: String, default: "페르소나 프로필 이미지 저장" }
  }

  connect() {
    this._handleKlavyAgentApply = this._handleKlavyAgentApply.bind(this)
    window.addEventListener("user-story-designer:apply-actions", this._handleKlavyAgentApply)
  }

  disconnect() {
    window.removeEventListener("user-story-designer:apply-actions", this._handleKlavyAgentApply)
    this._removeProgressBanner()
  }

  async _handleKlavyAgentApply(event) {
    const { data, resolve, reject } = event.detail || {}
    if (!data) return

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(`/main-target/projects/${this.projectIdValue}/klavy_agent_actions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ actions_data: data })
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const result = await res.json()

      if (resolve) resolve(result)

      window.location.reload()
    } catch (err) {
      console.error("[UserStoryTable] KlavyAgent apply failed:", err)
      if (reject) reject(err)
      window.location.reload()
    }
  }

  async _generatePersonaPhotos(personas, csrfToken) {
    const total = personas.length

    // 아바타마다 로딩 스피너 표시
    personas.forEach(p => {
      const el = document.querySelector(`.pp-card__avatar[data-persona-key="${p.key}"]`)
      if (el) {
        el.innerHTML = `<div class="pp-avatar-spinner"></div>`
        el.classList.add("pp-card__avatar--generating")
      }
    })

    // 진행 상태 배너 표시
    this._showProgressBanner(0, total)

    const results = {}
    let done = 0

    for (const persona of personas) {
      try {
        const res = await fetch("/main-target/ai/generate_image", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
          body: JSON.stringify({ prompt: persona.image_prompt, category: "portrait" })
        })
        const json = await res.json()
        if (json.success && json.imageUrl) {
          results[persona.key] = json.imageUrl
          // 완료된 아바타 즉시 업데이트
          const el = document.querySelector(`.pp-card__avatar[data-persona-key="${persona.key}"]`)
          if (el) {
            el.innerHTML = `<img src="${json.imageUrl}" alt="" style="width:100%;height:100%;object-fit:cover;" />`
            el.classList.remove("pp-card__avatar--generating")
          }
        }
      } catch (err) {
        console.error(`[UserStoryTable] 이미지 생성 실패 (${persona.key}):`, err)
        const el = document.querySelector(`.pp-card__avatar[data-persona-key="${persona.key}"]`)
        if (el) {
          el.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`
          el.classList.remove("pp-card__avatar--generating")
        }
      }
      done++
      this._showProgressBanner(done, total)
    }

    // 서버에 URL 저장
    if (Object.keys(results).length > 0) {
      try {
        await fetch(`/main-target/projects/${this.projectIdValue}/klavy_agent_actions`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken, "Accept": "application/json" },
          body: JSON.stringify({
            actions_data: {
              summary: this.msgPersonaSaveSummaryValue,
              actions: [{
                type: "set_persona_profiles",
                personas: Object.entries(results).map(([key, url]) => ({ key, photo_url: url }))
              }]
            }
          })
        })
      } catch (err) {
        console.error("[UserStoryTable] 이미지 URL 저장 실패:", err)
      }
    }

    this._showProgressBanner(total, total, true)
    setTimeout(() => window.location.reload(), 800)
  }

  _showProgressBanner(done, total, complete = false) {
    let banner = document.getElementById("us-image-gen-banner")
    if (!banner) {
      banner = document.createElement("div")
      banner.id = "us-image-gen-banner"
      banner.style.cssText = `
        position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
        background: #111827; color: #fff; padding: 12px 20px;
        font-size: 13px; font-weight: 600; letter-spacing: -0.01em;
        display: flex; align-items: center; gap: 10px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3); z-index: 9999;
        min-width: 260px; justify-content: center;
      `
      document.body.appendChild(banner)
    }

    if (complete) {
      banner.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366F1" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        ${this.msgProgressCompleteValue}
      `
      banner.style.background = "#1a1a2e"
    } else {
      const pct = total > 0 ? Math.round((done / total) * 100) : 0
      banner.innerHTML = `
        <div style="width:14px;height:14px;border:2px solid #6366F1;border-top-color:transparent;border-radius:50%;animation:spin 0.8s linear infinite;"></div>
        ${this.msgProgressGeneratingValue} ${done}/${total}
        <span style="color:#6366F1;font-weight:700;">${pct}%</span>
      `
    }

    if (!document.getElementById("us-spin-style")) {
      const style = document.createElement("style")
      style.id = "us-spin-style"
      style.textContent = `
        @keyframes spin { to { transform: rotate(360deg); } }
        .pp-avatar-spinner {
          width: 28px; height: 28px;
          border: 3px solid #C7D2FE;
          border-top-color: #4F46E5;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
      `
      document.head.appendChild(style)
    }
  }

  _removeProgressBanner() {
    document.getElementById("us-image-gen-banner")?.remove()
  }
}
